import React, { useContext, useState, useEffect, ReactNode } from 'react'

//Node Modules
import { Link } from 'react-router-dom'

//Components
import DocumentationLayout from 'components/DocumentationLayout'
import MainContentArea from 'components/MainContentArea'
import VersionPicker from 'components/VersionPicker'
import LimitedMenu from 'components/VersionPicker/LimitedMenu'
import CollectionItem from 'components/TOC/CollectionItem'
import SearchModal from 'components/SearchModal'

//Styles
import styles from './Collection.module.scss'
import './Collection.scss'

//Context
import { LanguageContext } from 'context/language-context'
import { RequestorContext, NodeInfoContext } from 'context/content-context'

//Types
import { CDC as ICDC, Collection as ICollection, DefaultContext } from 'types/cdc'
import IDocset from 'types/docset'
import {CollectionNode} from 'types/toc'
import { FilterOption } from 'types/search'

//Util
import { collection as collectionLink } from 'util/links'
import { siblings, flattenEntries } from 'util/toc'
import { DOCSET_PAGE_TITLEPAGE } from 'util/constants'
import {collectionDocsets} from 'util/search'
import { VERSION_LTS_IND } from 'util/constants'

//Hooks
import { useIsSmallDevice } from 'hooks'
import { setMetaDescription } from 'util/set-meta'

interface Props {
  collection: ICollection
  cdc?: ICDC
  currCdc: string | undefined
  docset?: IDocset
  currDocset: string | undefined
  page: string
  defaultContext: DefaultContext | undefined
  defaultPage: string | undefined
  content: React.ReactNode
  pdfUrl: string | undefined
  onPDFDownloadClick?: () => void
}

const Collection = ({collection, cdc, currCdc, docset, currDocset, page, defaultContext, defaultPage, content, pdfUrl, onPDFDownloadClick}:Props) => {
  const isSmallDevice = useIsSmallDevice()
  const lang = useContext(LanguageContext)
  const requestorId = useContext(RequestorContext)
  const nodeInfo = useContext(NodeInfoContext)
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(!isSmallDevice)
  const [showSearch, setShowSearch] = useState<boolean>(false)

  const { id:alias, version:cfr } = collection.info
  const { toc, versions } = collection
  const linkTitle = collection.info.title.value
  const cdcInfo = cdc &&  cdc.info
  const title = cdcInfo && cdcInfo.title.value
  const cdcTOC = cdc ? cdc.toc : []
  const docsetInfo = docset && docset.info
  const docsetAlias = docsetInfo && docsetInfo.id
  const cdcAlias = cdcInfo && cdcInfo.id
  const cdcVersion = (cdcInfo && cdcInfo.version) || ''
  const collDocsets = collectionDocsets(collection.cdcs) 

  const currentVersion = versions.find(v => v.version === cfr)

  const { prev, next } = siblings(flattenEntries(cdcTOC), docsetAlias, page)

  const prevLink = prev && collectionLink(lang, alias, cfr, cdcAlias, prev?.docset, prev.file)
  const nextLink = next && collectionLink(lang, alias, cfr, cdcAlias, next?.docset, next.file)
  const defaultLink = defaultContext && collectionLink(defaultContext.language, alias,cfr, cdcAlias, defaultContext.docset, defaultContext.file)
  const docsetLink = ():ReactNode => {
    //if current toc node is an embbed noded from a docset include
    if (nodeInfo.nodeInfo.embeddedNode )   {
      //if there is a parent
      if (nodeInfo.nodeInfo.parentTitle) {
        if (nodeInfo.nodeInfo.parentPage) { //if the parent node has a page assigned to it
          return <Link className={'breadcrumb-link'} to={collectionLink(lang, alias,cfr, cdcAlias, nodeInfo.nodeInfo.parentDocset, nodeInfo.nodeInfo.parentPage)}>{nodeInfo.nodeInfo.parentTitle}</Link>
        } else {
          return <span>{nodeInfo.nodeInfo.parentTitle}</span>
        }

      } else {  //no parent meaning the embedded node is a top level node
        return <></>
      }
    } 
    return <Link className={'breadcrumb-link'} to={collectionLink(lang, alias,cfr, cdcAlias, docsetAlias, DOCSET_PAGE_TITLEPAGE)}>{docsetInfo?.title.short}</Link>
  }
  

  const versionPickerMenu = ():ReactNode => {
    let versionItems = [] as ReactNode[];
    let unsupportedItems = [] as ReactNode[];
    versions.forEach(v => {
      if (!v.omit) {
        if (v.supportedInd) {
          versionItems.push(<VersionPicker.Item
            key={v.version}
            to={`./doc/${lang}/${alias}/${v.version}/${cdcAlias ? cdcAlias : currCdc}/${docsetAlias ? docsetAlias : currDocset}/${page}?homeOnFail${requestorId ? '&requestorId='+requestorId : ''}`}
            label={v.softwareVersion}
            active={v === currentVersion}
            cdc={true}
            lts={v.lts}
            ltsOverride={v.ltsOverride}
          />)
        } else {
          unsupportedItems.push(<VersionPicker.Item
            key={v.version}
            to={`./doc/${lang}/${alias}/${v.version}/${cdcAlias ? cdcAlias : currCdc}/${docsetAlias ? docsetAlias : currDocset}/${page}?homeOnFail${requestorId ? '&requestorId='+requestorId : ''}`}
            label={v.softwareVersion}
            active={v === currentVersion}
            cdc={true}
            lts={v.lts}
            ltsOverride={v.ltsOverride}
          />)
        }
        
      }
    })

    return (
      <>
        {versionItems}
        {unsupportedItems.length > 0 ?
          <LimitedMenu menuItems={unsupportedItems}/> : <></>
        }
      </>)
  }

  const versionPicker = (variant: "dark" | "light") => {
    if (currentVersion?.hide) return null;
    let show = versions.filter( version =>{
      if (version.hasOwnProperty('supportUrl')) return true;
      if (version.hasOwnProperty('omit')) {
        return !version.omit; 
      }
      return true
    }).length > 0 
    return show ? 
      <VersionPicker
        label={`${currentVersion?.lts && !currentVersion?.ltsOverride ? VERSION_LTS_IND : ''} ${(currentVersion && currentVersion.softwareVersion) || cdcVersion}`}
        variant={variant}
        menu={versionPickerMenu()}
        allUrl={(currentVersion && currentVersion.supportUrl) || versions[0].supportUrl || undefined}
      /> : <span className={`${variant} ${styles.emptyVersion}`}>{(currentVersion && currentVersion.softwareVersion) || cdcVersion}</span>
  }

  const toggleSidebar = () => {
    let el = document.getElementById('root')
    if (sidebarVisible) {
      el?.classList.add("showBanner")
      el?.classList.remove("hideBanner")
    } else {
      el?.classList.add('hideBanner')
      el?.classList.remove("showBanner")
    }
    setSidebarVisible(!sidebarVisible)
    setSidebarVisible(!sidebarVisible)
  }

  useEffect(() => {
    setSidebarVisible(!isSmallDevice)
  }, [isSmallDevice])

  useEffect(()=>{
    setMetaDescription(docset?.info.title.long || '')
  },[docset])

  let selected = currCdc
  let selectedTOC = cdcTOC

  const getFilterOptions = ():FilterOption[] => {
    let items: FilterOption[] = [];
    items = toc.map(t => {
      let tocCdc = t as CollectionNode
      let colCdc = collection.cdcs.find( c => {
        return c ? c.id === tocCdc.cdc : false
      })
      return colCdc ? {value: colCdc.id, display: colCdc.shortTitle.value } : {value: '', display:''}
    })
    return items
  }
  let index = 0
  return (
    <>
      <DocumentationLayout
        onSearchClick={() => setShowSearch(true)}
        sidebarVisible={sidebarVisible}
        toggleSidebar= {toggleSidebar}
        linkTitle={linkTitle}
        defaultLink = {defaultLink}
        title={title}
        version={currentVersion?.softwareVersion}
        versionPicker={versionPicker("dark")}
        info={docset && docset.info}
        pdf={pdfUrl} 
        onPDFDownloadClick={onPDFDownloadClick}
        side={
          <ul className={`${styles.collectionTOCContainer} collectionTOCContainer`}>
            { 
              toc.map( n => {
              index += 1
              let node = n as CollectionNode
              let cdcInfo = collection.cdcs.find ( cdc =>{  
                return cdc.id === node.cdc
              })
              return (
                <CollectionItem 
                  key={node.cdc}
                  alias={docsetAlias} 
                  page={page} 
                  selected={node.cdc === selected}
                  toc={selected ? selectedTOC : []}
                  cdcInfo={cdcInfo}
                  toggleSidebar={toggleSidebar}
                  index={index}
                  count={toc.length}/>
              )
            }) }
          </ul>
          
        }
        content={
          <MainContentArea
            prev={prevLink}
            next={nextLink}
            versionPicker={versionPicker("light")}
            title = {docsetLink}
            content={content}
          />
        }
      />
      {<SearchModal id={cdc?.info.id || ''} version={cdc?.info.version || ''} collectionId={alias} collectionVersion={cfr} currDocsetId={docset?.info.id || ''} currDocsetVersion={docset?.info.version || ''} currPage={page} type="collection" docsets={collDocsets} cdcs={collection.cdcs} show={showSearch} onHide={() => setShowSearch(!showSearch)} filterOptions={getFilterOptions()}/> }
    </>
  )
}

export default Collection